import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { DataFetcherService } from '../../services/data-fetcher/data-fetcher.service';
import * as mermaid from 'mermaid';
import { Inject, ChangeDetectorRef } from '@angular/core';
import { HeaderComponent } from '../../ui/header/header/header.component';
import { FooterComponent } from '../../ui/footer/footer/footer.component';
import { firstValueFrom } from 'rxjs';  // Import to convert Observable to Promise
import { ActivatedRoute } from '@angular/router';
import { RouterLink } from '@angular/router';
import { link } from 'node:fs';

@Component({
  selector: 'app-graph-view',
  standalone: true,
  imports: [CommonModule, HttpClientModule, HeaderComponent, FooterComponent, RouterLink],
  templateUrl: './graph-view.component.html',
  styleUrls: ['./graph-view.component.css']
})
export class GraphViewComponent implements OnInit {
  //Zoom functionality
  scale: number = 1;  // Initial zoom scale

  // Dragging functionality
  isDragging: boolean = false;
  startX: number = 0;
  startY: number = 0;
  offsetX: number = 0;
  offsetY: number = 0;


  workspaceId: string | undefined;
  workspaceName: string | undefined;

  private dataFetcher = inject(DataFetcherService);
  chartData: string = 'graph LR\n';
  isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
     // Access both the workspace ID and dataset ID from the route parameters
     this.route.params.subscribe(params => {
      this.workspaceId = params['workspaceId'];  // Workspace ID
    });

    if (this.isBrowser) {
      this.initializeMermaid();
      this.fetchData();
    }
  }

  // Handle zoom functionality with mouse wheel
  onZoom(event: WheelEvent): void {
    event.preventDefault();

    const zoomFactor = 0.1;
    if (event.deltaY < 0) {
      this.scale += zoomFactor; // Zoom in
    } else {
      this.scale -= zoomFactor; // Zoom out
    }

    // Restrict zoom level
    if (this.scale < 0.5) this.scale = 0.5;
    if (this.scale > 3) this.scale = 3;

    // Apply the scale to the graph
    const graph = document.getElementById('mermaidContainer');
    if (graph) {
      graph.style.transform = `scale(${this.scale}) translate(${this.offsetX}px, ${this.offsetY}px)`;
    }
  }

  // Handle drag functionality
  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startX = event.pageX - this.offsetX;
    this.startY = event.pageY - this.offsetY;

    // Add a class for dragging visual effect
    const graph = document.getElementById('mermaidContainer');
    if (graph) {
      graph.classList.add('dragging');
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    // Calculate the drag distance and apply to the graph position
    this.offsetX = event.pageX - this.startX;
    this.offsetY = event.pageY - this.startY;

    const graph = document.getElementById('mermaidContainer');
    if (graph) {
      graph.style.transform = `scale(${this.scale}) translate(${this.offsetX}px, ${this.offsetY}px)`;
    }
  }

  onMouseUp(): void {
    this.isDragging = false;

    // Remove dragging class
    const graph = document.getElementById('mermaidContainer');
    if (graph) {
      graph.classList.remove('dragging');
    }
  }

  async fetchData(): Promise<void> {
    try {
      const dataFetchPromises = [
        this.fetchDataflows(),
        this.fetchPipelines(),
        this.fetchDatasets(),
        this.fetchReports(),
        this.fetchSources(),
        this.fetchDashboards(),
        this.fetchWorkspace(),
      ];

      await Promise.all(dataFetchPromises);

      this.cdr.detectChanges();  // Ensure view is updated
      this.renderMermaid();      // Render chart after all data is fetched
    } catch (e) {
      console.error('Error while making graph', e);
    }
  }

  async fetchWorkspace(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getWorkspaceByIdData(this.workspaceId));
      if (Array.isArray(data.workspaces)) {
        this.workspaceName = data.workspaces[0].displayName
      } else {
        console.error('Unexpected data format:', data.workspaces);
      }
    } catch (error) {
      console.error('Error fetching workspace data:', error);
    }
  }

  async fetchDataflows(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getDataflowByWorkspaceIdData(this.workspaceId));
      if (Array.isArray(data.dataflows)) {
        this.chartData += this.convertToMermaidSyntax(data.dataflows, "dataflow", `<i class="fa-solid fa-wave-square"></i>`);
        console.log("Added dataflows to graph");
      } else {
        console.error('Unexpected data format:', data.dataflows);
      }
    } catch (error) {
      console.error('Error fetching dataflows data:', error);
    }
  }

  async fetchPipelines(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getPipelinesData());
      if (Array.isArray(data.pipelines)) {
        this.chartData += this.workspaceToMermaidSyntax(data.pipelines, "pipeline", `<i class="fa-solid fa-download"></i>`);
        console.log("Added pipelines to graph");
      } else {
        console.error('Unexpected data format:', data.pipelines);
      }
    } catch (error) {
      console.error('Error fetching pipelines data:', error);
    }
  }

  async fetchDashboards(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getDashboardByWorkspaceIdData(this.workspaceId));
      if (Array.isArray(data.dashboards)) {
        this.chartData += this.convertToMermaidSyntax(data.dashboards, "dashboard", `<i class="fa-solid fa-gauge-high"></i>`);
        console.log("Added dashboards to graph");
      } else {
        console.error('Unexpected data format:', data.dashboards);
      }
    } catch (error) {
      console.error('Error fetching dashboards data:', error);
    }
  }

  async fetchDatasets(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getDatasetsByWorkspaceIdData(this.workspaceId));
      if (Array.isArray(data.datasets)) {
        this.chartData += this.convertToMermaidSyntax(data.datasets, "dataset", `<i class="fa-solid fa-database"></i>`);
        console.log("Added datasets to graph");
      } else {
        console.error('Unexpected data format:', data.datasets);
      }
    } catch (error) {
      console.error('Error fetching datasets data:', error);
    }
  }

  async fetchReports(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getReportsByWorkspaceIdData(this.workspaceId));
      if (Array.isArray(data.reports)) {
        this.chartData += this.convertToMermaidSyntaxReport(data.reports, "report", `<i class="fa-solid fa-chart-bar"></i>`);
        console.log("Added reports to graph");
      } else {
        console.error('Unexpected data format:', data.reports);
      }
    } catch (error) {
      console.error('Error fetching reports data:', error);
    }
  }

  async fetchSources(): Promise<void> {
    try {
      const data = await firstValueFrom(this.dataFetcher.getSourcesByWorkspaceIdData(this.workspaceId));
      const linkData = await firstValueFrom(this.dataFetcher.getSourceLink(this.workspaceId));
      if (Array.isArray(data.sources) && Array.isArray(linkData)) {
        this.chartData += this.convertToMermaidSyntaxSource(data.sources, "source", `<i class="fa-solid fa-table-columns"></i>`);
        console.log("Added sources to graph");
        this.chartData += this.addSourceLink(linkData)
      } else {
        console.error('Unexpected data format:', data.sources);
      }
    } catch (error) {
      console.error('Error fetching sources data:', error);
    }
  }

  // Sanitize function to remove or escape special characters
 sanitizeMermaidString(input: string): string {
  return input.replace(/[\(\)\-]/g, '');  // Removes parentheses and hyphens
}

workspaceToMermaidSyntax(data: any[], type: string, icon: string): string {
  let mermaidString = '';
  data.forEach((item: any) => {
    const sanitizedDisplayName = this.sanitizeMermaidString(item.displayName);
    // URL for each item
    const url = `${window.location.origin}/${type.toLowerCase()}/${item.id}`;
    // Assign a class based on the type and make the node clickable
    mermaidString += `${item.id}[${icon} ${sanitizedDisplayName}]:::${type.toLowerCase()}\n`;
    // Add the click directive with the URL
    mermaidString += `click ${item.id} "${url}" _blank\n`;  // _blank opens in a new tab
  });
  return mermaidString;
}

convertToMermaidSyntax(data: any[], type: string, icon: string): string {
  let mermaidString = '';
  data.forEach((item: any) => {
    const sanitizedDisplayName = this.sanitizeMermaidString(item.displayName);
    // URL for each item
    const url = `${window.location.origin}/workspace/${this.workspaceId}/${type.toLowerCase()}/${item.id}`;
    // Assign a class based on the type and make the node clickable
    mermaidString += `${item.id}[${icon} ${sanitizedDisplayName}]:::${type.toLowerCase()}\n`;
    // Add the click directive with the URL
    mermaidString += `click ${item.id} "${url}" _blank\n`;  // _blank opens in a new tab
  });
  return mermaidString;
}

convertToMermaidSyntaxReport(data: any[], type: string, icon: string): string {
  let mermaidString = '';
  data.forEach((item: any) => {
    const sanitizedDisplayName = this.sanitizeMermaidString(item.displayName);
    // URL for each item
    const url = `${window.location.origin}/workspace/${this.workspaceId}/${type.toLowerCase()}/${item.id}`;
    // Assign a class based on the type and make the node clickable
    mermaidString += `${item.id}[${icon} ${sanitizedDisplayName}]:::${type.toLowerCase()} \n`;
    mermaidString += `${item.datasetId} --> ${item.id} \n`;
    mermaidString += `click ${item.id} "${url}" _blank\n`;  // _blank opens in a new tab

  });
  return mermaidString;
}

convertToMermaidSyntaxSource(data: any[], type: string, icon: string): string {
  let mermaidString = '';
  data.forEach((item: any) => {
    const sanitizedDisplayName = this.sanitizeMermaidString(item.dataSourceType);
    // URL for each item
    const url = `${window.location.origin}/workspace/${this.workspaceId}/${type.toLowerCase()}/${item.datasourceId}`;
    // Assign a class based on the type and make the node clickable
    mermaidString += `${item.datasourceId}[${icon} ${sanitizedDisplayName}]:::${type.toLowerCase()} \n`;
    // Add the click directive with the URL
    mermaidString += `click ${item.datasourceId} "${url}" _blank\n`;  // _blank opens in a new tab
  });
  return mermaidString;
}

addSourceLink(data: any[]): string {
  let mermaidString = '';
  data.forEach((item: any) => {
      mermaidString += `${item.sourcesDatasourceId} --> ${item.datasetsId} \n`;
  });
  return mermaidString;
}



  initializeMermaid(): void {
    if (this.isBrowser) {
      mermaid.default.initialize({
        startOnLoad: false
      });
    }
  }

  renderMermaid(): void {
    if (this.isBrowser) {
      const container = document.getElementById('mermaidContainer');
      if (container) {
        // Inject custom class definitions for colors and shapes
        this.chartData += this.getMermaidStyles();
        container.innerHTML = this.chartData;
        mermaid.default.run();
      }
    }
  }

  // Define Mermaid styles for different types
  getMermaidStyles(): string {
    return `
      classDef dataflow fill:#F6C1C1,stroke:#F1A8A8,stroke-width:2px;
      classDef source fill:#A2C2E0,stroke:#8DA6C8,stroke-width:2px;
      classDef pipeline fill:#F5E1A4,stroke:#E6C476,stroke-width:2px;
      classDef dashboard fill:#BFD8B8,stroke:#A3C29C,stroke-width:2px;
      classDef dataset fill:#D1B6E4,stroke:#B18BC9,stroke-width:2px;
      classDef report fill:#FFE1D6,stroke:#FFB19D,stroke-width:2px;
    `;
  }

  //Dataflow = Red
  //Source = Blue
  //Pipeline = Yellow
  //Dashboard = Green
  //Dataset = Lavender
  //Report = Peach
}
